import { useEffect, useState } from "react";
import { Plus, FileDown, Trash2, Loader2, ClipboardList, Pencil } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { pdfUrl } from "../../lib/pdfUrl";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import ImageUploadField from "../../components/ImageUploadField";
import SignedPdfCell from "../../components/SignedPdfCell";
import ReportSuccessPanel from "../../components/ReportSuccessPanel";
import { useAuth } from "../../context/AuthContext";

const empty = {
  customer_name: "", contact_person: "", contact_number: "",
  po_number: "",
  reference_no: "",
  vehicle_type: "", vehicle_number: "", serial_number: "",
  // products: array of product names. The backend `description` field is
  // built by joining this with ", " on submit. Loaded from existing report's
  // description (split by ", ") when editing.
  products: [],
  product_notes: "", system_description: "",
  device_installed: "",
  date: "", inspected_by: "",
  installation_date: "",
  remark: "",
  before_image: "", after_image: "",
};

// ── Safety-equipment product catalog (Approach A) ───────────────────────
// Each product can have ordered option "dims". A dim's `choices(sel)` may
// depend on earlier selections (cascading) — e.g. AFDSS nozzle depends on
// zone + capacity. Selected products are composed into a single string:
//   "Name (opt1 / opt2 / opt3)"  — no commas inside, so the PDF (which splits
//   the description by ",") keeps each product on its own row.
const SAFETY_PRODUCTS = [
  { name: "Rear View Camera System", dims: [{ key: "Channel", choices: () => ["2 Channel", "4 Channel", "AI Based"] }] },
  { name: "Audio Visual Alarm", dims: [] },
  { name: "Auto Dipping System / Dipper", dims: [] },
  { name: "Dala Raise Warning Control System", dims: [
      { key: "Option", choices: () => ["With Interlock", "Without Interlock", "Body Lifting Voice with Controller"] },
  ] },
  { name: "Driver Fatigue Monitoring System", dims: [{ key: "Mode", choices: () => ["Offline", "Online"] }] },
  { name: "Proximity Warning Device", dims: [
      { key: "Range", choices: () => ["10 Mtr", "30 Mtr"] },
      { key: "Sensor", choices: () => ["1 Sensor", "Dual Sensor"] },
  ] },
  { name: "Anti Tilt Device", nameIsOption: true, dims: [
      { key: "Device", choices: () => [
        "Anti Tilt Device",
        "Body Lifting Controller",
      ] },
  ] },
  { name: "Seat Belt Voice Reminder", dims: [] },
  { name: "Battery Cut Off Switch", dims: [] },
  { name: "Biometric Authentication System", dims: [] },
  { name: "Tyre Pressure Sensor (TPMS)", dims: [] },
  { name: "Beacon Light", dims: [
      { key: "Type", choices: () => ["Blinking", "Rotating"] },
      { key: "Color", choices: () => ["Red", "Yellow", "Blue"] },
  ] },
  { name: "Hand Brake Alert System", dims: [] },
  { name: "LED Safety Flag Whips", dims: [] },
  { name: "Tramming Alarm", dims: [] },
  { name: "Load Indicator", dims: [] },
  { name: "Pre-Start Alarm", dims: [] },
  { name: "Backup Alarm", dims: [] },
  { name: "Semi AFDSS", dims: [
      { key: "Capacity", choices: () => ["16 lbs", "25 lbs"] },
      { key: "Nozzle", choices: (s) => ({ "16 lbs": ["4 nozzle"], "25 lbs": ["6 nozzle"] }[s.Capacity] || []) },
  ] },
  { name: "Manual AFDSS", dims: [
      { key: "Capacity", choices: () => ["16 lbs", "25 lbs"] },
      { key: "Nozzle", choices: (s) => ({ "16 lbs": ["4 nozzle"], "25 lbs": ["6 nozzle"] }[s.Capacity] || []) },
  ] },
  { name: "Automatic AFDSS", dims: [
      { key: "Zone", choices: () => ["Single Zone", "Dual Zone"] },
      { key: "Capacity", choices: () => ["16 lbs", "25 lbs", "50 lbs"] },
      { key: "Nozzle", choices: (s) => {
          const m = {
            "Single Zone": { "16 lbs": ["6 nozzle"], "25 lbs": ["6 nozzle", "8 nozzle"], "50 lbs": ["6 nozzle", "8 nozzle"] },
            "Dual Zone": { "16 lbs": ["6+6 nozzle"], "25 lbs": ["6+6 nozzle", "6+8 nozzle"], "50 lbs": ["6+6 nozzle", "6+8 nozzle"] },
          };
          return ((m[s.Zone] || {})[s.Capacity]) || [];
      } },
  ] },
  { name: "PONTOON BODY POSITIONING MONITORING SYSTEM  (ON-LINE/GPRS/SMS based system)", dims: [] },
];

// Default selection for a product: each dim = its first valid choice given
// the choices already made for earlier dims.
const defaultSel = (dims) => {
  const sel = {};
  for (const d of dims) { const ch = d.choices(sel); sel[d.key] = ch[0] || ""; }
  return sel;
};

// Compose one selected product into its display/storage string.
const composeProduct = (p, sel) => {
  if (!p.dims || !p.dims.length) return p.name;
  // "Chooser" products: the dropdown value IS the product name (no wrapping).
  if (p.nameIsOption) return (sel || {})[p.dims[0].key] || p.name;
  const vals = p.dims.map((d) => (sel || {})[d.key]).filter(Boolean);
  return vals.length ? `${p.name} (${vals.join(" / ")})` : p.name;
};

// All currently-selected products as composed strings, each with quantity
// appended as " x N" (kept parseable so the PDF can show a Qty column).
const composeSelected = (prodSel) =>
  SAFETY_PRODUCTS.filter((p) => prodSel[p.name]).map((p) => {
    const base = composeProduct(p, prodSel[p.name]);
    const q = parseInt((prodSel[p.name] || {}).__qty, 10) || 1;
    return `${base} x ${q}`;
  });

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-900 mb-3 pb-1 border-b border-slate-200">
      {title}
    </h3>
    {children}
  </div>
);

// Defined at module scope so its identity is stable across re-renders — this
// prevents the input from losing focus on every keystroke (the classic
// "type 1 letter, focus lost" bug caused by inline-defined components).
const Field = ({ label, value, onChange, required, type = "text", placeholder, rows, testId }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">
      {label}{required && " *"}
    </Label>
    {rows ? (
      <Textarea rows={rows} required={required} value={value} onChange={onChange}
        placeholder={placeholder} className="rounded-none resize-y" data-testid={testId} />
    ) : (
      <Input type={type} required={required} value={value} onChange={onChange}
        placeholder={placeholder} className="rounded-none h-10" data-testid={testId} />
    )}
  </div>
);

export default function VehicleReportsAdmin() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  // Manually added product rows ("Add Row") — free-text product names that
  // are not (or need not be) in the catalog. Merged with the catalog
  // selections (form.products) on submit so they all appear in the PDF.
  const [extraProducts, setExtraProducts] = useState([]);
  // prodSel: { [productName]: { [dimKey]: value } }. A product is "ticked"
  // when its name is a key here. Drives the Approach-A product picker.
  const [prodSel, setProdSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/reports/vehicle").then((r) => setItems(r.data));
  const loadProducts = () =>
    api.get("/products").then((r) => setProducts(r.data || []))
       .catch(() => setProducts([]));
  useEffect(() => { load(); loadProducts(); }, []);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Toggle a product on/off in the picker.
  const toggleProduct = (p, checked) => {
    setProdSel((prev) => {
      const next = { ...prev };
      if (checked) next[p.name] = { ...defaultSel(p.dims), __qty: 1 };
      else delete next[p.name];
      return next;
    });
  };

  // Set the quantity for a selected product (minimum 1).
  const setQty = (p, val) => {
    const q = Math.max(1, parseInt(val, 10) || 1);
    setProdSel((prev) => ({ ...prev, [p.name]: { ...(prev[p.name] || {}), __qty: q } }));
  };

  // Change one option dim; reset all downstream dims to their first valid
  // choice (so cascading stays consistent).
  const updateDim = (p, dimKey, val) => {
    setProdSel((prev) => {
      const cur = { ...(prev[p.name] || {}) };
      cur[dimKey] = val;
      let passed = false;
      for (const d of p.dims) {
        if (passed) {
          const ch = d.choices(cur);
          if (!ch.includes(cur[d.key])) cur[d.key] = ch[0] || "";
        }
        if (d.key === dimKey) passed = true;
      }
      return { ...prev, [p.name]: cur };
    });
  };

  const selectedComposed = composeSelected(prodSel);

  // Pontoon body monitoring system is NOT installed on a vehicle, so when
  // selected the Vehicle No / Make / Reference No fields are disabled (and
  // cleared on submit). Matched loosely on the word "PONTOON".
  const isPontoon = [...selectedComposed, ...extraProducts.map((r) => r.name || "")].some(
    (p) => (p || "").toUpperCase().includes("PONTOON")
  );

  const openNew = () => {
    setCreated(null);
    setEditingId(null);
    setExtraProducts([]);
    setProdSel({});
    setForm({ ...empty, inspected_by: user?.name || "" });
    setOpen(true);
  };

  const openEdit = (r) => {
    setCreated(null);
    setEditingId(r.id);
    const allNames = (r.description || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => {
        // Split trailing " x N" (quantity) back into name + qty.
        const m = s.match(/^(.*?)\s*[x\u00d7]\s*(\d+)\s*$/i);
        return m ? { name: m[1].trim(), qty: parseInt(m[2], 10) || 1 } : { name: s, qty: 1 };
      });
    // Existing products are loaded as editable "Add Row" entries so they
    // round-trip exactly (the cascading picker is used for adding new ones).
    setProdSel({});
    setExtraProducts(allNames);
    setForm({
      customer_name: r.customer_name || "",
      contact_person: r.contact_person || "",
      contact_number: r.contact_number || "",
      po_number: r.po_number || "",
      reference_no: r.reference_no || "",
      vehicle_type: r.vehicle_type || "",
      vehicle_number: r.vehicle_number || "",
      serial_number: r.serial_number || "",
      products: [],
      product_notes: r.product_notes || "",
      system_description: r.system_description || "",
      device_installed: r.device_installed || "",
      date: r.date || "",
      inspected_by: r.inspected_by || "",
      installation_date: r.installation_date || "",
      remark: r.remark || "",
      before_image: r.before_image || "",
      after_image: r.after_image || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Join selected products into a single comma-separated string for
      // backend (`description`). PDF generator re-splits by ", " to render
      // them as a numbered list. Both the catalog selections and the
      // manually added "Add Row" entries are included so every product the
      // user entered in the form shows up in the PDF.
      const { products, ...rest } = form;
      const merged = [
        ...selectedComposed,
        ...extraProducts.map((r) => {
          const n = (r.name || "").trim();
          return n ? `${n} x ${Math.max(1, parseInt(r.qty, 10) || 1)}` : "";
        }),
      ].filter(Boolean);
      // De-duplicate while preserving order.
      const allProducts = merged.filter((n, i) => merged.indexOf(n) === i);
      if (allProducts.length === 0) {
        toast.error("Please select at least one product");
        setBusy(false);
        return;
      }
      const payload = {
        ...rest,
        description: allProducts.join(", "),
      };
      // Pontoon system is not vehicle-mounted → clear vehicle fields so they
      // stay blank in the saved report and PDF.
      if (allProducts.some((p) => p.toUpperCase().includes("PONTOON"))) {
        payload.vehicle_number = "";
        payload.vehicle_type = "";
        payload.reference_no = "";
      }
      if (editingId) {
        const { data } = await api.put(`/reports/vehicle/${editingId}`, payload);
        toast.success(`Report ${data.report_no} updated`);
        setCreated(data); load();
      } else {
        const { data } = await api.post("/reports/vehicle", payload);
        toast.success(`Report ${data.report_no} created`);
        setCreated(data); load();
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const closeDialog = () => { setOpen(false); setCreated(null); setEditingId(null); load(); };

  const del = async (r) => {
    if (!window.confirm(`Delete ${r.report_no}?`)) return;
    if (user.role !== "admin") return toast.error("Admin only");
    try {
      await api.delete(`/reports/vehicle/${r.id}`);
      toast.success("Deleted"); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-inspection-reports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">Field Reports</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-3">
            <ClipboardList className="w-6 h-6 text-amber-500" /> Safety Equipment Installation Reports
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Single-page form for fast field entry on phone or laptop.
          </p>
        </div>
        <Button data-testid="new-inspection-report-btn" onClick={openNew} className="rounded-none btn-amber font-semibold h-10">
          <Plus className="w-4 h-4 mr-2" /> New Safety Equipment Installation Report
        </Button>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3 font-semibold">Report No</th>
              <th className="text-left p-3 font-semibold">Vehicle</th>
              <th className="text-left p-3 font-semibold">System</th>
              <th className="text-left p-3 font-semibold">Customer</th>
              <th className="text-left p-3 font-semibold">Installation By</th>
              <th className="text-left p-3 font-semibold">Date</th>
              <th className="text-left p-3 font-semibold">Signed PDF</th>
              <th className="text-right p-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-200">
                <td className="p-3 font-mono text-xs">{r.report_no}</td>
                <td className="p-3 font-semibold">{r.vehicle_number}<div className="text-xs text-slate-500 font-normal">{r.vehicle_type}</div></td>
                <td className="p-3">{r.system_description || r.device_installed}</td>
                <td className="p-3">{r.customer_name}</td>
                <td className="p-3">{r.inspected_by || r.submitted_by || "—"}</td>
                <td className="p-3 font-mono text-xs">{r.date}</td>
                <td className="p-3"><SignedPdfCell report={r} endpoint="/reports/vehicle" onUploaded={load} /></td>
                <td className="p-3 text-right space-x-1">
                  {r.pdf_url && (
                    <a href={pdfUrl(r)} target="_blank" rel="noopener noreferrer"
                       className="inline-flex p-2 hover:bg-slate-100" title="Download PDF">
                      <FileDown className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => openEdit(r)}
                    className="inline-flex p-2 hover:bg-amber-50 text-amber-700"
                    title="Edit & regenerate PDF"
                    data-testid={`edit-inspection-${r.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {user.role === "admin" && (
                    <button onClick={() => del(r)} className="p-2 hover:bg-red-50 text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-12 text-center text-slate-500 text-sm">No inspection reports yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v) closeDialog(); else setOpen(true); }}>
        <DialogContent
          className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto"
          // Keep the form open until the user explicitly clicks the Close (X)
          // button (or Cancel). Accidental clicks outside the modal, or
          // pressing Escape, must NOT close it or discard entered data.
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-heading">
              {created ? "Report Saved" : (editingId ? "Edit Safety Equipment Installation Report" : "New Safety Equipment Installation Report")}
            </DialogTitle>
          </DialogHeader>
          {created ? (
            <ReportSuccessPanel report={created} endpoint="/reports/vehicle" onDone={closeDialog} onUploaded={load} />
          ) : (
          <form onSubmit={submit} className="mt-2" data-testid="inspection-report-form">
            {/* Warranty notice — shown ONLY here during form fill-up. This is
                intentionally NOT rendered on the generated PDF. */}
            <div
              className="mb-4 bg-amber-50 border border-amber-200 px-4 py-3 text-sm italic text-amber-900 text-center"
              data-testid="warranty-notice"
            >
              In order that we may fulfill obligations of the warranty on this Vehicle Safety Device,
              please complete the information requested below.
            </div>
            {/* Top header strip — Ref No (auto) | PO Number | Date */}
            <div className="mb-5 grid grid-cols-3 gap-3 bg-slate-50 border border-slate-200 p-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1 block">Ref No (Auto Generated)</Label>
                <div className="h-10 px-3 flex items-center font-mono text-sm bg-white border border-slate-200" data-testid="ref-no-display">
                  {editingId ? (items.find((x) => x.id === editingId)?.report_no || "—") : "Auto on submit"}
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1 block">PO Number / Bill No</Label>
                <Input value={form.po_number} onChange={setField("po_number")}
                  className="rounded-none h-10" placeholder="PO-2026-001 / Bill 12345"
                  data-testid="insp-po-number" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1 block">Date *</Label>
                <Input type="date" value={form.date} onChange={setField("date")} required
                  className="rounded-none h-10" data-testid="insp-date" />
              </div>
            </div>

            <Section title="Customer">
              <Field value={form.customer_name} onChange={setField("customer_name")} label="Name & Address (Customer)" required rows={2}
                placeholder="Western Coalfields Ltd, Bellora, Walgaon Deep OCM…" testId="insp-customer" />
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <Field value={form.contact_person} onChange={setField("contact_person")} label="Contact Person" placeholder="Dharmendra Gupta" />
                <Field value={form.contact_number} onChange={setField("contact_number")} label="Contact Number" placeholder="9657585028" />
              </div>
            </Section>

            <Section title="Product & Vehicle Details">
              {/* Vehicle row — Vehicle No | Reference No (auto) | Make.
                  Uses the same 3-column strip styling as the
                  Ref No | PO/Bill No | Date strip at the top of the form. */}
              <div className="mb-5 grid grid-cols-3 gap-3 bg-slate-50 border border-slate-200 p-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1 block">Vehicle No</Label>
                  <Input value={isPontoon ? "" : form.vehicle_number} onChange={setField("vehicle_number")} required={!isPontoon} disabled={isPontoon}
                    className="rounded-none h-10 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
                    placeholder={isPontoon ? "N/A for Pontoon" : "28069, 28070"}
                    data-testid="insp-vehicle-no" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1 block">Reference No</Label>
                  <Input value={isPontoon ? "" : form.reference_no} onChange={setField("reference_no")} disabled={isPontoon}
                    className="rounded-none h-10 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
                    placeholder={isPontoon ? "N/A for Pontoon" : "Enter reference no"}
                    data-testid="insp-reference-no" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1 block">Make</Label>
                  <Input value={isPontoon ? "" : form.vehicle_type} onChange={setField("vehicle_type")} required={!isPontoon} disabled={isPontoon}
                    className="rounded-none h-10 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
                    placeholder={isPontoon ? "N/A for Pontoon" : "Tata, Ashok Leyland"}
                    data-testid="insp-make" />
                </div>
              </div>
              {isPontoon && (
                <p className="-mt-3 mb-4 text-xs text-amber-700">
                  Pontoon system selected — Vehicle No, Make &amp; Reference No are not applicable and have been disabled.
                </p>
              )}

              {/* Product Name — multi-select. Engineers can tick multiple
                  products installed at the same site and they all appear in
                  the same generated report. List loads from /products. */}
              <div className="mb-4">
                <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">
                  Product Name(s) *
                </Label>
                <div
                  className="border border-slate-200 rounded-none bg-white max-h-72 overflow-y-auto"
                  data-testid="insp-product-name-list"
                >
                  {SAFETY_PRODUCTS.map((p) => {
                    const sel = prodSel[p.name];
                    const checked = !!sel;
                    return (
                      <div key={p.name} className="border-b border-slate-100">
                        <label className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm">
                          <Checkbox checked={checked} onCheckedChange={(c) => toggleProduct(p, c)} />
                          <span>{p.name}</span>
                        </label>
                        {checked && (
                          <div className="flex flex-wrap items-end gap-3 px-3 pb-3 pl-9">
                            {p.dims.map((d) => {
                              const choices = d.choices(sel);
                              return (
                                <div key={d.key} className="flex flex-col gap-0.5">
                                  <span className="text-[10px] uppercase tracking-wider text-slate-400">{d.key}</span>
                                  <select
                                    value={sel[d.key] || ""}
                                    onChange={(e) => updateDim(p, d.key, e.target.value)}
                                    className="h-9 min-w-[130px] border border-slate-300 rounded-none px-2 text-sm bg-white"
                                    data-testid={`opt-${p.name}-${d.key}`}
                                  >
                                    {choices.map((c) => (
                                      <option key={c} value={c}>{c}</option>
                                    ))}
                                  </select>
                                </div>
                              );
                            })}
                            <div className="flex flex-col gap-0.5">
                              <span className="text-[10px] uppercase tracking-wider text-amber-600 font-bold">Quantity</span>
                              <input
                                type="number"
                                min="1"
                                value={sel.__qty || 1}
                                onChange={(e) => setQty(p, e.target.value)}
                                className="h-9 w-24 border border-amber-300 rounded-none px-2 text-sm bg-white"
                                data-testid={`qty-${p.name}`}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {selectedComposed.length > 0 && (
                  <div className="mt-2 text-xs text-slate-500" data-testid="selected-products-summary">
                    Selected ({selectedComposed.length}): {selectedComposed.join(", ")}
                  </div>
                )}
              </div>

              {/* Add Row — lets the engineer add extra product names directly
                  so multiple products can be added quickly in the same report.
                  These are merged with the catalog selections on submit and
                  all appear in the generated PDF. */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold block">
                    Additional Products
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setExtraProducts((rows) => [...rows, { name: "", qty: 1 }])}
                    className="rounded-none h-8 text-xs"
                    data-testid="add-product-row"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Row
                  </Button>
                </div>
                {extraProducts.length === 0 ? (
                  <p className="text-xs text-slate-400">
                    Click "Add Row" to enter products that are not in the list above.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {extraProducts.map((val, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`product-row-${idx}`}>
                        <Input
                          value={val.name}
                          onChange={(e) =>
                            setExtraProducts((rows) =>
                              rows.map((r, i) => (i === idx ? { ...r, name: e.target.value } : r))
                            )
                          }
                          placeholder="Product name"
                          className="rounded-none h-10"
                          data-testid={`product-row-input-${idx}`}
                        />
                        <input
                          type="number"
                          min="1"
                          value={val.qty}
                          onChange={(e) =>
                            setExtraProducts((rows) =>
                              rows.map((r, i) => (i === idx ? { ...r, qty: Math.max(1, parseInt(e.target.value, 10) || 1) } : r))
                            )
                          }
                          className="rounded-none h-10 w-20 border border-amber-300 px-2 text-sm"
                          title="Quantity"
                          data-testid={`product-row-qty-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setExtraProducts((rows) => rows.filter((_, i) => i !== idx))
                          }
                          className="p-2 hover:bg-red-50 text-red-600 border border-slate-200"
                          title="Remove row"
                          data-testid={`remove-product-row-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Optional Product Details — single textarea, applies to all
                  selected products. Renders on the PDF only when non-empty. */}
              <div className="mb-4">
                <Field
                  value={form.product_notes}
                  onChange={setField("product_notes")}
                  label="Optional Product Details"
                  rows={2}
                  placeholder="e.g. Back camera installed, cable replaced, additional wiring completed"
                  testId="insp-product-notes"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field value={form.system_description} onChange={setField("system_description")} label="System Description" required placeholder="Proximity Warning Device" />
              </div>
            </Section>

            <Section title="Installation Remarks">
              {/* Standard remarks — always printed on the PDF, shown read-only here */}
              <div className="bg-blue-50/40 border border-blue-200 p-4 text-sm leading-relaxed" data-testid="insp-standard-remarks">
                <p className="text-[11px] uppercase tracking-widest font-bold text-blue-900 mb-2">
                  Standard Remarks (always included on the PDF)
                </p>
                <ol className="list-decimal pl-5 space-y-1 text-slate-700">
                  <li>As per your requirement, we have installed our device.</li>
                  <li>All devices are working normally and in good condition.</li>
                  <li>Warranty: 12 Months from the date of installation against any manufacturing defects.</li>
                </ol>
              </div>
              <div className="mt-4">
                <Field value={form.remark} onChange={setField("remark")}
                  label="Additional Remarks (optional, customer-specific)"
                  rows={4}
                  placeholder="Any extra notes or observations specific to this installation…"
                  testId="insp-remark" />
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold block mb-1">Installation Date</Label>
                  <Input type="date" value={form.installation_date} onChange={setField("installation_date")}
                    className="rounded-none h-10" data-testid="insp-installation-date" />
                </div>
                <Field value={form.inspected_by} onChange={setField("inspected_by")} label="Installation By" required testId="insp-installation-by" />
              </div>
            </Section>

            <Section title="Photos">
              <div className="grid md:grid-cols-2 gap-4">
                <ImageUploadField label="Before Image" value={form.before_image}
                  onChange={(v) => setForm({ ...form, before_image: v })} testId="insp-before" />
                <ImageUploadField label="After Image" value={form.after_image}
                  onChange={(v) => setForm({ ...form, after_image: v })} testId="insp-after" />
              </div>
              <div className="mt-4 bg-amber-50 border border-amber-200 p-4 text-xs text-amber-800">
                <strong>Signatures:</strong> After generating the PDF, print it, collect Site Engineer &amp;
                UEE Technician signatures, scan and upload using the <em>Upload Signed PDF</em> button.
              </div>
            </Section>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <Button type="button" variant="outline" onClick={closeDialog} className="rounded-none">Cancel</Button>
              <Button data-testid="insp-submit" type="submit" disabled={busy} className="rounded-none btn-amber font-semibold">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingId ? "Save & Regenerate PDF" : "Submit & Generate PDF"}
              </Button>
            </div>
          </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
